/**
 * Platform Admin Hook
 * Checks if the current user is a platform administrator
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../config/environment';

export function usePlatformAdmin() {
  const { accessToken, user } = useAuth();
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const checkPlatformAdmin = async () => {
      if (!accessToken || !user) {
        setIsPlatformAdmin(false);
        setIsLoading(false);
        return;
      }

      try {
        const apiBaseUrl = getApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/platform/admins`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          // If we can successfully fetch the admin list, we are a platform admin
          console.log('✅ Platform admin check: SUCCESS - User IS a platform admin');
          if (mounted) {
            setIsPlatformAdmin(true);
            setIsLoading(false);
          }
        } else if (response.status === 403) {
          // Not a platform admin
          console.log('❌ Platform admin check: 403 - User is NOT a platform admin');
          if (mounted) {
            setIsPlatformAdmin(false);
            setIsLoading(false);
          }
        } else {
          console.log(`⚠️ Platform admin check: ${response.status} - ${response.statusText}`);
          throw new Error(`Failed to check platform admin status: ${response.statusText}`);
        }
      } catch (err: any) {
        console.error('Error checking platform admin status:', err);
        if (mounted) {
          setError(err.message);
          setIsPlatformAdmin(false);
          setIsLoading(false);
        }
      }
    };

    checkPlatformAdmin();

    return () => {
      mounted = false;
    };
  }, [accessToken, user]);

  return { isPlatformAdmin, isLoading, error };
}

